<template>
  <div>
    <ReportSkeleton v-if="reportsLoading" />
    <div v-else>
      <div v-if="!reportsLoading && hasReports">
        <div class="d-flex justify-end">
          <Button
            v-if="hasSomeGeneratedExpiredReport"
            plain
            class="mr-2 mb-2 font-size-14"
            size="small"
            color="primary"
            data-testid="reportsList-button-clearAll"
            :loading="loading"
            :text="$t('insights.clear_the_clutter')"
            @click="deleteAllExpiredReports"
          />
        </div>
        <div v-for="report in reports" :key="report._id">
          <v-sheet
            rounded
            elevation="2"
            class="py-4 px-6 mb-4"
            :class="{
              highlighted:
                Boolean(reportNotificationId) &&
                reportNotificationId === report._id,
            }"
          >
            <v-row class="ma-0">
              <v-col cols="9" class="pa-0">
                <p
                  v-if="isGeneratedReportExpired(report.expiresAt)"
                  class="caption mb-1 expired-message"
                >
                  {{
                    $t('insights.expired', {
                      date: formatDate(report.expiresAt, 'MMM D, YYYY'),
                    })
                  }}
                </p>
                <p v-else class="caption blue-grey--text mb-1">
                  <span class="mr-2">
                    {{
                      $t('insights.requested', {
                        date: formatDate(
                          report.createdAt,
                          'MMMM D, YYYY hh:mm a'
                        ),
                      })
                    }}</span
                  >
                  <span
                    v-if="willExpireInOneDay(report.expiresAt)"
                    class="expires-one-day-message"
                  >
                    <v-icon small color="yellow" class="mr-1"
                      >mdi-alert-circle-outline</v-icon
                    >{{ $t('insights.expires_in_one_day') }}</span
                  >
                </p>
                <p class="body-1 blue-grey--text text--darken-3 mb-0">
                  {{ fileName(report) }}
                </p>
              </v-col>
              <v-col cols="3" class="pa-0 d-flex justify-end align-center">
                <div
                  v-if="
                    report.status === REPORT_STATUS.inProgress &&
                      !isTheReportGeneratedFailed(report.generationExpiresAt)
                  "
                >
                  <v-chip small data-private class="blue lighten-2">
                    <span class="caption blue-grey--text text--darken-3">{{
                      $t('insights.in_progress')
                    }}</span>
                  </v-chip>
                </div>
                <div v-else>
                  <v-btn
                    v-if="
                      (report.status === REPORT_STATUS.inProgress &&
                        isTheReportGeneratedFailed(
                          report.generationExpiresAt
                        )) ||
                        report.status === REPORT_STATUS.error
                    "
                    icon
                    small
                    class="mx-2"
                    data-testid="reportsList-button-alert"
                  >
                    <v-icon color="red">mdi-alert-circle-outline</v-icon>
                  </v-btn>
                  <template v-else>
                    <Button
                      v-if="!isGeneratedReportExpired(report.expiresAt)"
                      plain
                      class="mr-2"
                      color="primary"
                      icon="mdi-download-circle-outline"
                      data-testid="reportsList-button-download"
                      @click="$emit('downloadXlsxReport', report)"
                    />
                  </template>
                  <Button
                    color="blueGrey"
                    size="small"
                    :icon="'mdi-close'"
                    data-testid="reportsList-button-delete"
                    @click="$emit('deleteReport', report._id)"
                  />
                </div> </v-col
            ></v-row>
          </v-sheet>
        </div>
      </div>
      <EmptyState
        v-else
        class="pa-8"
        :title="$t('insights.you_have_not_to_create_any_reports_yet')"
        :message="$t('insights.getting_started')"
      />
    </div>
  </div>
</template>

<script>
import api from '@/api'
import { utils } from 'frontend-js-shared'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import Button from '@/components/UI/Button'
import EmptyState from '@/components/EmptyState.vue'
import mixin from '@/mixins/mixin.vue'
import moment from 'moment'
import ReportSkeleton from './ReportSkeleton.vue'

const ONE_DAY_IN_TIMESTAMP_FORMAT = 86400
const REPORT_STATUS = {
  inProgress: 'inProgress',
  completed: 'completed',
  error: 'error',
}

export default {
  name: 'ReportsList',

  mixins: [mixin, trackerMixin],

  components: {
    Button,
    EmptyState,
    ReportSkeleton,
  },

  props: {
    organizationId: {
      type: String,
      required: false,
    },
    reports: {
      type: Array,
      required: true,
    },
    reportId: {
      type: String,
      required: false,
    },
    reportsLoading: Boolean,
  },

  data() {
    return {
      REPORT_STATUS,
      readyReportAlert: false,
      loading: false,
      reportNotificationId: null,
    }
  },

  async mounted() {
    this.reportNotificationId = this.getRouteQuery()?.reportId
      ? this.getRouteQuery()?.reportId
      : this.reportId
  },

  computed: {
    hasReports() {
      return Boolean(this.reports.length > 0)
    },

    hasSomeGeneratedExpiredReport() {
      return this.reports.some(
        report =>
          this.isGeneratedReportExpired(report.expiresAt) ||
          (this.isTheReportGeneratedFailed(report.generationExpiresAt) &&
            report?.status === REPORT_STATUS.inProgress) ||
          report?.status === REPORT_STATUS.error
      )
    },
  },

  methods: {
    formatDate(date, format) {
      return utils.dates.formatDate(date, format)
    },

    async deleteAllExpiredReports() {
      try {
        this.loading = true
        const reportsIds = this.reports
          .filter(
            report =>
              this.isGeneratedReportExpired(report?.expiresAt) ||
              (this.isTheReportGeneratedFailed(report?.generationExpiresAt) &&
                report?.status === REPORT_STATUS.inProgress) ||
              report?.status === REPORT_STATUS.error
          )
          .map(report => report._id)
        const payload = {
          ids: reportsIds,
          operation: 'delete',
        }
        await api.report.deleteAllExpiredReports(this.organizationId, payload)
        this.trackClearReportList({ ids: reportsIds })
        this.$emit('listGeneratedReports')
      } catch (err) {
        this.showError(
          this.$t(`error.generic_errors.${err?.response?.status}`, {
            details: err,
          })
        )
      } finally {
        this.loading = false
      }
    },

    filterReportsNotDownloaded(id) {
      return this.reports.filter(report => report._id !== id)
    },

    fileName(report) {
      if (!startDate && !endDate) return report.reportType
      const startDate = moment(report.filters.startDate)
      const endDate = moment(report.filters.endDate)
      return `${report.reportType} ${this.formatDate(
        startDate,
        'MMM D, YYYY'
      )} - ${this.formatDate(endDate, 'MMM D, YYYY')}`
    },

    isGeneratedReportExpired(expiresAt) {
      const now = moment().unix()
      const expiredOn = moment(expiresAt).unix()
      return Boolean(expiredOn - now <= 0)
    },

    isTheReportGeneratedFailed(generationExpiresAt) {
      const now = moment().unix()
      const expiredOn = moment(generationExpiresAt).unix()
      return Boolean(expiredOn - now <= 0)
    },

    willExpireInOneDay(expiresAt) {
      const now = moment().unix()
      const expiredOn = moment(expiresAt).unix()
      return Boolean(expiredOn - now <= ONE_DAY_IN_TIMESTAMP_FORMAT)
    },
  },
}
</script>
<style lang="scss" scoped>
.expired-message {
  color: $color-red;
}

.expires-one-day-message {
  color: $color-yellow;
}

.highlighted {
  animation: highlight 7000ms ease-out;
}

@keyframes highlight {
  0% {
    background-color: var(--v-blue-lighten2);
  }
  100% {
    background-color: white;
  }
}
</style>
